<template>
  <v-app>
    <v-app-bar app>
      <v-btn icon @click="$router.go(-1)">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $t("header") }}</v-toolbar-title>
      <v-btn @click="toggleLocale"
        >{{ $t("language") }} ({{ $i18n.locale }})</v-btn
      >
    </v-app-bar>
    <v-container>
      <v-form @submit.prevent="register" ref="form" v-model="valid">
        <v-row>
          <v-col cols="12" md="6" class="mx-auto">
            <v-select
              v-model="tyreNumber"
              :items="tyreNumberOptions"
              :rules="[rules.required]"
              required
            >
              <template v-slot:label>
                <span class="select-label"
                  >{{ $t("tyreQuantity") }}
                  <span class="required">*</span></span
                >
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row v-if="tyreNumber > 1">
          <v-col cols="12" md="6" class="mx-auto">
            <v-img
              src="/Capture.PNG"
              max-height="100"
              contain
              class="my-3"
            ></v-img>
          </v-col>
        </v-row>

        <!-- New section for QR code uploads -->
        <v-row v-if="parseInt(tyreNumber) > 1" :key="`qr-section-${qrCodeKey}`">
          <v-col cols="12" md="6" class="mx-auto">
            <h3>{{ $t("scanAdditionalQRCodes") }}</h3>
            <v-row
              v-for="(_, i) in qrCodeInputs"
              :key="`qr-${i}-${qrCodeKey}`"
              align="center"
            >
              <v-col cols="9">
                <v-text-field v-model="qrInputValues[i]"></v-text-field>
              </v-col>
              <v-col cols="3">
                <v-btn
                  color="primary"
                  @click="validateQRCode(i)"
                  :loading="uploadingQR[i]"
                  :disabled="!qrInputValues[i]"
                >
                  {{ $t("newTyre") }}
                </v-btn>
              </v-col>
            </v-row>
            <!-- Error Dialog -->
            <v-dialog v-model="showErrorDialog" max-width="300">
              <v-card>
                <v-card-title class="headline">{{ $t("error") }}</v-card-title>
                <v-card-text>{{ errorMessage }}</v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text @click="showErrorDialog = false">
                    {{ $t("ok") }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6" class="mx-auto">
            <h2>{{ $t("purchasedTyreSize") }}</h2>
            <v-simple-table>
              <template v-slot:default>
                <tbody>
                  <!-- First row always shows the initial tire info -->
                  <tr>
                    <td class="tyre-info-cell">
                      <div class="tyre-info-line">{{ tyreInfo.tireline }}</div>
                      <div class="tyre-info-size">{{ tyreInfo.tireSize }}</div>
                    </td>
                  </tr>
                  <!-- Additional rows based on API responses -->
                  <template v-if="parseInt(tyreNumber) > 1">
                    <tr v-for="i in parseInt(tyreNumber) - 1" :key="i">
                      <td
                        class="tyre-info-cell"
                        v-if="
                          qrCodeResponses[i - 1] &&
                          qrCodeResponses[i - 1].result
                        "
                      >
                        <div class="tyre-info-line">
                          {{ qrCodeResponses[i - 1].result.tireline }}
                        </div>
                        <div class="tyre-info-size">
                          {{ qrCodeResponses[i - 1].result.tireSize }}
                        </div>
                      </td>
                      <td class="tyre-info-cell" v-else>
                        <div class="tyre-info-line">{{}}</div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
        <h3>{{ $t("contactInfo") }}</h3>
        <v-row>
          <v-col cols="12" md="6" class="mx-auto">
            <v-text-field v-model="name" :rules="[rules.required]" required>
              <template v-slot:label>
                <span>{{ $t("name") }} <span class="required">*</span></span>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" class="mx-auto">
            <v-text-field
              v-model="phone"
              :rules="[rules.required, rules.phone]"
              required
            >
              <template v-slot:label>
                <span>{{ $t("phone") }} <span class="required">*</span></span>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" class="mx-auto">
            <v-text-field v-model="email" type="email" :rules="emailRules">
              <template v-slot:label>
                <span>{{ $t("email") }}</span>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" class="mx-auto">
            <v-text-field
              v-model="vehicleRegistrationNo"
              :rules="[rules.required]"
              required
            >
              <template v-slot:label>
                <span
                  >{{ $t("carLicensePlate")
                  }}<span class="required">*</span></span
                >
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <h3>{{ $t("purchaseInfo") }}</h3>
        <v-row>
          <v-col cols="12" md="6" class="mx-auto">
            <v-text-field
              v-model="dealerName"
              :rules="[rules.required]"
              required
            >
              <template v-slot:label>
                <span
                  >{{ $t("dealerName") }} <span class="required">*</span></span
                >
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" class="mx-auto">
            <Datepicker
              v-model="purchaseDate"
              :rules="[rules.required]"
              format="yyyy-MM-dd"
              :placeholder="$t('purchaseDate')"
              :input-class="'custom-datepicker-input'"
            >
              <template v-slot:label>
                <span
                  >{{ $t("purchaseDate") }}
                  <span class="required">*</span></span
                >
              </template>
            </Datepicker>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" class="mx-auto">
            <v-file-input
              v-if="showFileInputs"
              :key="`attachment-${fileInputKey}`"
              v-model="attachment"
              :label="$t('purchaseReceipt')"
              :rules="[rules.required]"
              required
              accept="image/*"
            >
              <template v-slot:label>
                <span
                  >{{ $t("purchaseReceipt") }}
                  <span class="required">*</span></span
                >
              </template>
            </v-file-input>
          </v-col>
        </v-row>

        <!-- Terms and Submit Button -->
        <v-row>
          <v-col cols="12" md="6" class="mx-auto">
            <v-checkbox
              v-model="termsAccepted"
              :rules="[rules.required]"
              required
            >
              <template v-slot:label>
                <span>{{ $t("terms") }}<span class="required">*</span></span>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>

        <!-- Submit Button -->
        <v-row>
          <v-col cols="12" class="mx-auto">
            <v-btn
              color="primary"
              type="submit"
              :disabled="!valid || isLoading"
              :loading="isLoading"
              >{{ $t("submit") }}</v-btn
            >
          </v-col>
        </v-row>
      </v-form>
    </v-container>

    <v-dialog v-model="showReferenceDialog" max-width="300">
      <v-card>
        <v-card-title class="headline">{{
          $t("registrationSuccess")
        }}</v-card-title>
        <v-card-text> {{ $t("registrationReferenceNumber") }} </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeReferenceDialogAndGoBack">
            {{ $t("ok") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import axios from "axios";

export default {
  components: { Datepicker },
  props: {
    uuid: {
      type: String,
      required: true,
    },
  },
  mounted() {
    this.fetchTyreInfo();
    window.scrollTo(0, 0);
  },
  computed: {
    tyreNumberOptions() {
      return [1, 2, 3, 4];
    },
    validQRCodeResponses() {
      return this.qrCodeResponses.filter(
        (response) => response && response.success && response.result
      );
    },
    qrCodeInputs() {
      return Array(parseInt(this.tyreNumber)).fill("");
    },
    additionalTyres() {
      return Array(Math.max(0, parseInt(this.tyreNumber) - 1));
    },
  },
  data() {
    return {
      valid: false,
      tyreNumber: "1",
      name: "",
      email: "",
      phone: "",
      vehicleRegistrationNo: "",
      dealerName: "",
      tyrePattern: "",
      tyreSize: "",
      purchaseDate: "",
      termsAccepted: false,
      attachment: null,
      tyreInfo: {
        tyreSize: "",
        tyrePattern: "",
      },
      emailRules: [
        (value) => !value || /.+@.+\..+/.test(value) || this.$t("invalidEmail"),
      ],
      rules: {
        required: (value) => !!value || this.$t("required"),
        phone: (value) => {
          if (!value) return this.$t("required");
          if (!/^\d{8}$/.test(value)) return this.$t("invalidPhone");
          return true;
        },
      },
      isLoading: false,
      qrInputValues: [],
      uploadingQR: [],
      qrCodeResponses: [],
      scannedUUIDs: [],
      errorMessage: "",
      showErrorDialog: false,
      showFileInputs: true,
      qrCodeKey: 0,
      referenceNumber: "",
      showReferenceDialog: false,
      fileInputKey: 0,
    };
  },
  watch: {
    tyreNumber: {
      immediate: true,
      handler(newValue, oldValue) {
        const newCount = parseInt(newValue);
        const oldCount = oldValue ? parseInt(oldValue) : 0;

        // When changing from 1 to multiple tyres
        if (parseInt(newValue) > 1 && parseInt(oldValue) === 1) {
          // Clear all QR codes
          this.scannedUUIDs = [];
          this.qrInputValues = Array(newCount).fill("");
          this.qrCodeResponses = Array(newCount).fill(null);
          this.uploadingQR = Array(newCount).fill(false);

          // Clear the first tyre info
          this.tyreInfo = {
            tireline: "",
            tireSize: "",
          };

          // Force re-render of QR code inputs
          this.qrCodeKey++;
          return;
        }

        // When changing from multiple tyres back to 1
        if (parseInt(newValue) === 1 && parseInt(oldValue) > 1) {
          // Re-fetch the original tyre info
          this.fetchTyreInfo();
          return;
        }

        if (newCount > oldCount) {
          // Extend arrays with empty values for new slots
          this.qrInputValues = [
            ...(this.qrInputValues || []),
            ...Array(newCount - oldCount).fill(""),
          ];
          this.qrCodeResponses = [
            ...(this.qrCodeResponses || []),
            ...Array(newCount - oldCount).fill(null),
          ];
          this.uploadingQR = [
            ...(this.uploadingQR || []),
            ...Array(newCount - oldCount).fill(false),
          ];
        } else if (newCount < oldCount) {
          // Trim arrays when reducing tyre count
          this.qrInputValues = this.qrInputValues.slice(0, newCount);
          this.qrCodeResponses = this.qrCodeResponses.slice(0, newCount);
          this.uploadingQR = this.uploadingQR.slice(0, newCount);
        }
      },
    },
  },
  methods: {
    closeReferenceDialogAndGoBack() {
      this.showReferenceDialog = false;
      this.$router.go(-1);
    },
    toggleLocale() {
      this.$i18n.locale = this.$i18n.locale === "en" ? "zh" : "en";
    },
    async fetchTyreInfo() {
      try {
        const response = await axios.get(
          `https://tyremanagementwebhost20240620094429.azurewebsites.net/api/services/app/Configuration/GetTyreInfo?uuid=${this.$route.params.uuid}`
        );
        this.tyreInfo = response.data.result;
        // Push the UUID from route params instead of the API response
        this.scannedUUIDs.push(this.$route.params.uuid);
      } catch (error) {
        console.error("Error fetching tyre info:", error);
      }
    },
    async register() {
      this.isLoading = true;
      if (!this.$refs.form.validate()) {
        this.isLoading = false;
        return;
      }

      if (!this.purchaseDate) {
        alert(this.$t("missingPurchaseDate"));
        this.isLoading = false;
        return;
      }

      if (!this.attachment) {
        alert(this.$t("missingPurchaseReceipt"));
        this.isLoading = false;
        return;
      }

      const formData = new FormData();

      formData.append("Name", this.name);
      formData.append("Email", this.email);
      formData.append("Phone", this.phone);
      formData.append("VehicleRegistrationNo", this.vehicleRegistrationNo);
      formData.append("TyreQuantity", this.tyreNumber);
      formData.append("PurchaseDate", this.purchaseDate.toISOString());
      formData.append("DealerName", this.dealerName);

      if (this.attachment) {
        formData.append("Attachment", this.attachment);
      }

      this.scannedUUIDs.forEach((uuid) => {
        if (uuid) {
          formData.append("Uuids", uuid);
        }
      });

      try {
        const response = await axios.post(
          "https://tyremanagementwebhost20240620094429.azurewebsites.net/api/services/app/Configuration/PostTyreRegistration",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              accept: "text/plain",
              Authorization: "null",
            },
          }
        );

        if (response.data && response.data.success) {
          this.referenceNumber = response.data.result.referenceNumber;
          this.showReferenceDialog = true;
          this.resetForm();
        } else {
          alert(this.$t("registrationFailed"));
        }
      } catch (error) {
        console.error("Registration error:", error);
        if (error.response) {
          console.error("Error data:", error.response.data);
          console.error("Error status:", error.response.status);
          console.error("Error headers:", error.response.headers);
          alert(`${this.$t("registrationError")}: ${error.response.status}`);
        } else if (error.request) {
          console.error("Error request:", error.request);
          alert(this.$t("noResponse"));
        } else {
          console.error("Error message:", error.message);
          alert(this.$t("registrationError"));
        }
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      window.scrollTo(0, 0);
      // Hide file inputs
      this.showFileInputs = false;

      // Manually reset form fields
      this.name = "";
      this.email = "";
      this.phone = "";
      this.vehicleRegistrationNo = "";
      this.tyreNumber = "1";
      this.dealerName = "";
      this.purchaseDate = null;
      this.termsAccepted = false;

      // Reset arrays and file inputs
      this.attachment = null;
      this.qrCodeAttachments = [];
      this.qrCodeResponses = [];
      this.uploadingQR = [];
      this.scannedUUIDs = [];

      // Increment fileInputKey to force re-creation of file inputs
      this.fileInputKey++;

      // Use nextTick to ensure the DOM has updated before showing file inputs again
      this.$nextTick(() => {
        this.showFileInputs = true;
      });

      // Manually reset validation
      if (this.$refs.form) {
        Object.keys(this.$refs.form.errors).forEach((key) => {
          this.$refs.form.errors[key] = [];
        });
      }
    },
    async validateQRCode(index) {
      if (!this.qrInputValues[index]) return;
      this.uploadingQR[index] = true;
      this.errorMessage = "";
      try {
        const response = await axios.get(
          `https://tyremanagementwebhost20240620094429.azurewebsites.net/api/services/app/Configuration/GetTyreInfoForShortId?uuid=${this.qrInputValues[index]}`
        );

        if (response.data && response.data.success) {
          const fullUUID = response.data.result.uuid;

          if (response.data.result.isRegister) {
            this.errorMessage = this.$t("tyreAlreadyRegistered");
            this.showErrorDialog = true;
            return;
          }

          if (this.scannedUUIDs.includes(fullUUID)) {
            this.errorMessage = this.$t("duplicateQRCode");
            this.showErrorDialog = true;
          } else {
            // If it's the first input (index 0), update the tyreInfo
            if (index === 0) {
              this.tyreInfo = {
                tireline: response.data.result.tireline,
                tireSize: response.data.result.tireSize,
              };
              // Replace the first UUID if it exists, otherwise add it
              if (this.scannedUUIDs.length > 0) {
                this.scannedUUIDs[0] = fullUUID;
              } else {
                this.scannedUUIDs.push(fullUUID);
              }
            } else {
              this.scannedUUIDs.push(fullUUID);
              this.qrCodeResponses[index - 1] = response.data; // Adjust index for responses
            }
          }
        } else {
          this.errorMessage = this.$t("invalidQRCode");
          this.showErrorDialog = true;
        }
      } catch (error) {
        console.error(`Error validating QR code ${index + 1}:`, error);
        this.errorMessage = this.$t("noTyreRecord");
        this.showErrorDialog = true;
      } finally {
        this.uploadingQR[index] = false;
      }
    },
  },
};
</script>

<style scoped>
.required {
  color: red;
}

.select-label {
  font-size: 18px;
}

:deep(.v-select .v-field__input) {
  font-size: 24px !important;
}

:deep(.v-menu .v-list-item) {
  font-size: 18px !important;
  min-height: 48px !important;
}

:deep(.v-menu .v-list-item__content) {
  font-size: 18px !important;
}

.tyre-info-cell {
  padding: 12px;
  border: 1px solid #e0e0e0;
}

.tyre-info-line {
  font-weight: bold;
  margin-bottom: 4px;
  font-size: 25px;
}

.tyre-info-size {
  color: #666;
  font-size: 20px;
  font-weight: bold;
}

.tyre-id {
  font-size: 15px;
}
</style>
